import React, { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import Spinner from '../../ui/spinner';
import clientReviewViewModel from '../../../view-model/client/client-review-view-model';
import ReviewStats from './review-stats';
import ReviewList from './review-list';

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  professionalId: string;
  professionalName?: string;
}

/**
 * Modal d'affichage des avis d'un professionnel
 */
const ReviewModal: React.FC<ReviewModalProps> = observer(({ 
  isOpen, 
  onClose, 
  professionalId,
  professionalName 
}) => {
  const { t } = useTranslation();
  const [localProfessionalName, setLocalProfessionalName] = useState<string>(professionalName || '');
  
  // Utilisation du view-model pour les données
  const loading = clientReviewViewModel.isLoading;
  const error = clientReviewViewModel.error;
  const reviews = clientReviewViewModel.professionalReviews;
  const stats = clientReviewViewModel.professionalStats;
  
  // Charger les données lorsque le modal s'ouvre
  useEffect(() => {
    if (isOpen && professionalId) {
      // Réinitialiser les états
      clientReviewViewModel.resetStates();
      
      // Charger les avis et statistiques
      const loadData = async () => {
        await clientReviewViewModel.fetchProfessionalReviews(professionalId);
        await clientReviewViewModel.fetchProfessionalStats(professionalId);
        
        // Extraire le nom du professionnel du premier avis si disponible et non fourni
        if (!professionalName && clientReviewViewModel.professionalReviews.length > 0 && 
            clientReviewViewModel.professionalReviews[0].professional) {
          const { firstName, lastName, businessName } = clientReviewViewModel.professionalReviews[0].professional;
          setLocalProfessionalName(businessName || `${firstName} ${lastName}`);
        }
      };
      
      loadData();
    }
  }, [isOpen, professionalId, professionalName]);
  
  // Référence pour détecter les clics à l'extérieur du modal
  const modalRef = useRef<HTMLDivElement>(null);
  
  // Gérer le clic à l'extérieur du modal pour le fermer
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Empêcher le défilement du body quand le modal est ouvert
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Restaurer le défilement quand le modal est fermé
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);
  
  // Gérer la touche Escape pour fermer le modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div 
        ref={modalRef}
        className="w-full max-w-3xl bg-white rounded-2xl p-6 shadow-xl relative animate-fadeIn"
      >
        <h3 className="text-2xl font-bold text-primary text-center mb-4">
          {t('reviews.titleReview')}
        </h3>
                
                <button
                  type="button"
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
                  onClick={onClose}
                >
                  <span className="sr-only">{t('common.close', 'Fermer')}</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                
                <div className="mt-4">
                  {loading ? (
                    <div className="flex justify-center py-12">
                      <Spinner size="lg" />
                    </div>
                  ) : error ? (
                    <div className="bg-white rounded-xl p-8 text-center">
                      <p className="text-red-500 mb-4">{error}</p>
                      <button 
                        className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
                        onClick={() => {
                          if (professionalId) {
                            clientReviewViewModel.resetStates();
                            clientReviewViewModel.fetchProfessionalReviews(professionalId);
                            clientReviewViewModel.fetchProfessionalStats(professionalId);
                          }
                        }}
                      >
                        {t('common.retry', 'Réessayer')}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
                      {/* Résumé des statistiques */}
                      {stats && <ReviewStats stats={stats} />}
                      
                      {/* Liste des avis avec pagination */}
                      <ReviewList reviews={reviews} loading={loading} />
                    </div>
                  )}
                </div>
      </div>
    </div>
  );
});

export default ReviewModal;
